import { DatePipe, Location, NgClass } from '@angular/common';
import { AfterViewInit, Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap/modal';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  PasAccordionBodyDirective,
  PasAccordionComponent,
  PasButtonComponent,
  PasCheckboxComponent,
  PasConfirmationService,
  PasDateTimeComponent,
  PasInputFieldComponent,
  PasInputNumberComponent,
  PasLayoutSidebarHolderComponent,
  PasNotificationsComponent,
  PasSelectComponent,
  PasSelectOption,
  PasSidebarHandleService,
  PasSpinnerComponent,
  PasSystemProcessComponent,
  PasTextareaComponent,
} from '@porsche-aftersales/frontend-toolbox';
import { GridApi, GridOptions } from 'ag-grid-community';
import { cloneDeep, isEmpty, isEqual, isNil, isString, startsWith } from 'lodash-es';
import moment from 'moment';
import { Observable, Subject, takeUntil } from 'rxjs';

import { SupplierDto } from '../../../api/tur-rc/models/supplier-dto';
import { SupplierService } from '../../../api/tur-rc/services/supplier.service';
import { DecimalFormatConverter, UserLanguage } from '../../../core/decimal-format-converter';
import { DateFormatPipe } from '../../../pipes/date-format.pipe';
import { AttachmentService } from '../../../services/attachment/attachment.service';
import {
  bookingStatusUpdateNeeded,
  createDeleteAttachmentAllowed,
  newAttachmentModel,
} from '../../../services/attachment/attachments-functions';
import { BookingApprovalsHistoryService } from '../../../services/booking-approvals-history/booking-approvals-history.service';
import { BookingService } from '../../../services/booking/booking.service';
import {
  canUserApproveBooking,
  checkBookingRightsForRecourseType,
  checkBookingStateAndUserRights,
  handleVerification,
  indexGenerator,
  isBookingCancelable,
  isCustomerCurrencyEuro,
  isManipulated,
  newFormDataForAttachment,
  showAttachmentUploadFailed,
  showAttachmentUploadSuccess,
  translateBookingState,
} from '../../../services/booking/recourse-booking-functions';
import { RecourseBookingStore } from '../../../services/booking/recourse-booking-store';
import { CurrencyService } from '../../../services/currency/currency.service';
import { AmountModel } from '../../../services/currency/model/amount-model';
import { BookingRoutingService } from '../../../services/details-view-service/booking-routing.service';
import { CampaignRoutingService } from '../../../services/details-view-service/campaign-routing.service';
import { LoggerService } from '../../../services/logging-service/logger.service';
import { NotificationServiceWrapper } from '../../../services/notification-service-wrapper/notification-service-wrapper';
import { RouterService } from '../../../services/router-service/router-service';
import { UserRights } from '../../../services/user-service/user-rights.enum';
import { UserService } from '../../../services/user-service/user.service';
import { ApproveRejectReprocessDialog } from '../../../shared/approve-reject-reprocess-dialog/approve-reject-reprocess-dialog.component';
import { BookingStateEnum } from '../../../shared/enums/booking-state-enum';
import { BookingTypeEnum } from '../../../shared/enums/booking-type-enum';
import {
  extractEnumValues,
  getEnumByValue,
  getEnumStringValue,
} from '../../../shared/enums/enum-util-functions/enum-utils';
import { RecourseTypeIdEnum } from '../../../shared/enums/recourse-type-id-enum';
import { TopicEnum } from '../../../shared/enums/topic-enum';
import { GenericModalContentComponent } from '../../../shared/generic-modal-content/generic-modal-content.component';
import {
  dropdownValuesHandler,
  getBookingTypeDropdownValue,
  getDropdownValue,
  getDropdownValues,
} from '../../../shared/model-serializer/mapper-functions';
import { BookingApprovalsHistoryComponent } from '../booking-approvals-history/booking-approvals-history.component';
import { BookingAttachmentsComponent } from '../booking-attachments/booking-attachments.component';
import { AttachmentModel } from '../model/attachment.model';
import { BookingApprovalsHistoryModel } from '../model/booking-approvals-history-model';
import { BookingPartialPaymentModel } from '../model/booking-partial-payment-model';
import { RecourseBookingCUModel } from '../model/recourse-booking-cu-model';
import { RecourseBookingModel } from '../model/recourse-booking-model';
import { UploadFileModel } from '../model/upload-file-model';
import { ReferenceBookingsComponent } from '../reference-bookings/reference-bookings.component';

export const MAX_FILE_UPLOAD_SIZE = 15_728_640;

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'recourse-booking-add-edit-details',
  templateUrl: './recourse-booking-add-edit-details.component.html',
  styleUrls: ['./recourse-booking-add-edit-details.component.scss'],
  imports: [
    TranslatePipe,
    PasSystemProcessComponent,
    ReactiveFormsModule,
    DatePipe,
    PasAccordionBodyDirective,
    PasAccordionComponent,
    PasSelectComponent,
    PasTextareaComponent,
    PasInputFieldComponent,
    PasDateTimeComponent,
    PasCheckboxComponent,
    ReferenceBookingsComponent,
    BookingApprovalsHistoryComponent,
    BookingAttachmentsComponent,
    PasNotificationsComponent,
    PasLayoutSidebarHolderComponent,
    NgClass,
    GenericModalContentComponent,
    FormsModule,
    ApproveRejectReprocessDialog,
    PasSpinnerComponent,
    PasButtonComponent,
    PasInputNumberComponent,
  ],
})
export class RecourseBookingAddEditDetailsComponent implements OnInit, AfterViewInit {
  private readonly notificationServiceWrapper = inject(NotificationServiceWrapper);
  private readonly sidebarHandleService = inject(PasSidebarHandleService);
  private readonly translateService = inject(TranslateService);
  private readonly bookingService = inject(BookingService);
  private readonly recourseBookingStore = inject(RecourseBookingStore);
  private readonly pasConfirmationService = inject(PasConfirmationService);
  private readonly userService = inject(UserService);
  private readonly loggerService = inject(LoggerService);
  private readonly supplierService = inject(SupplierService);
  private readonly modalService = inject(NgbModal);
  private readonly attachmentService = inject(AttachmentService);
  private readonly approvalsHistoryService = inject(BookingApprovalsHistoryService);
  private readonly currencyService = inject(CurrencyService);
  private readonly location = inject(Location);
  private readonly routerService = inject(RouterService);
  private readonly bookingRoutingService = inject(BookingRoutingService);
  private readonly campaignRoutingService = inject(CampaignRoutingService);
  public readonly dateFormatPipe = inject(DateFormatPipe);

  @ViewChild(ReferenceBookingsComponent)
  public referenceBookingsComponent: ReferenceBookingsComponent;

  @ViewChild('ModalSpinnerTemplate') public modalSpinnerTemplate: ElementRef;
  public modalSpinner: NgbModalRef;

  @ViewChild('processComponent')
  private readonly processComponent: PasSystemProcessComponent;

  @ViewChild('approvalsHistoryComponent')
  private readonly approvalsHistoryComponent: BookingApprovalsHistoryComponent;

  @ViewChild('bookingAttachmentsComponent')
  private readonly bookingAttachmentsComponent: BookingAttachmentsComponent;

  private referencedBooking: RecourseBookingModel;

  @ViewChild(ApproveRejectReprocessDialog)
  public approveRejectReprocessDialog: ApproveRejectReprocessDialog;

  public readonly compCommentCharactersMaxLength: number = 1000;
  public compCommentCharactersLeft = 1000;

  private readonly savedSubject$ = new Subject<void>();

  public formGroup: UntypedFormGroup;
  public formattedCalculatedAmount: string[] = [];
  public formattedTotalCalculatedAmount = '';
  public debitor = '';
  public explicitPaymentTarget = false;
  public referringCurrencyVisible: boolean[] = [];
  public bookingCorrupted = false;
  public bookingRejectionCommentVisible = false;
  public bookingStateReasonLabel = '';
  public bookingStateBlockedOrFailed = false;
  public recourseBookingObservable: Observable<RecourseBookingModel>;
  public supplier: SupplierDto;
  private savedBookingIds: any[] = [];
  public initialFormGroupAndArrayFields: any;
  public bookingTypeDropdownValues: any[] = [];
  public editorCurrencyDropdownValues: any[] = [];
  public recourseTypeDropdownValues: any[] = [];
  public selectedBookingType: any;
  public selectedRecourseType: any;
  public selectedEditorCurrency: any;
  public bookingLoaded = false;
  public startApprovalBtnHidden = true;
  public startApprovalBtnDisabled = true;
  // when bookingCurrency and customerCurrency are different
  public currencyBookingAndCustomerNotEqual = false;
  public formChanged = false;
  public formInitialized = false;
  public recourseTypeLabel = '';
  public bookingTypeLabel = '';
  public validationMessages: { error: string; message: string }[];
  public indexGenerator: any;

  @ViewChild('UploadFileDialogTemplate')
  public uploadFileDialogTemplate: ElementRef;
  public uploadFileDialog: NgbModalRef;
  public acceptanceAllowed = false;
  public isBookingValid = false;
  public uploadFileModel: UploadFileModel = new UploadFileModel();
  public filesToUpload: UploadFileModel[] = [];
  public selectedTopic: any;
  public attachments: AttachmentModel[] = [];
  public bookingAttachmentsAccordionClosed = true;
  public referenceBookings: RecourseBookingModel[] = [];
  public referenceAccordionClosed = true;
  public bookingApprovalsHistory: BookingApprovalsHistoryModel[] = [];
  public bookingApprovalsHistoryAccordionClosed = true;
  public topicDropdownValues: any[] = [];
  public decimalSeparator: ',' | '.' = '.';
  public externalReceiverCheckboxDisabled = false;
  public selectedBookingDetails: RecourseBookingModel;
  public gridOptions: GridOptions;
  public gridApi: GridApi;
  public columnDefs;
  public defaultColDef;
  public frameworkComponents;
  public spinnerActive = false;
  private customerCurrency = '';
  private calculatedEUR: number[] = [];
  private calculatedForign: number[] = [];
  private totalCalculatedEUR: number;
  private totalCalculatedForign: number;
  public readonly maxFileUploadSize: number = MAX_FILE_UPLOAD_SIZE;
  public partialPaymentFormArray: UntypedFormArray;
  public partialPaymentList: BookingPartialPaymentModel[] = [];

  private static readonly SELECTED_CURRENCY = 'selectedCurrency';
  private static readonly CUSTOMER_CURRENCY = 'customerCurrency';

  public errorMessages: string[];

  public ngOnInit(): void {
    this.initializeView();
    this.checkIfInMeantimeManipulated();
  }

  public async initializeView(): Promise<void> {
    this.referencedBooking = this.recourseBookingStore.newCorrectionBookingData;
    this.bookingLoaded = false;

    // TOOD: following line: we have this on X places. it's repetetive and is quite error prone (if new language is introduced)
    this.decimalSeparator = this.userService.userLanguage.startsWith('en') ? '.' : ',';
    this.indexGenerator = indexGenerator();
    this.setTopicDropdownValues();

    this.initValidationMessages();
    this.setFormValues();

    this.defaultColDef = {
      resizable: true,
      sortable: false,
      suppressHeaderMenuButton: true,
    };

    this.setBookingTypeDropdownValues();
    this.setEditorCurrencyDropdownValues();

    if (this.isEditOrViewTabActive()) {
      this.selectedBookingDetails = this.recourseBookingStore.selectedRecourseBooking;
      this.populateForm(this.selectedBookingDetails);
      if (this.selectedBookingDetails.bookingStateId === BookingStateEnum.FAILED) {
        this.translateService
          .get(['BOOKING_DETAILS_DIALOG_FAILED_TITLE', 'OK'])
          .subscribe((translate: { [key: string]: any }) => {
            const title: string = translate.BOOKING_DETAILS_DIALOG_FAILED_TITLE;
            const message: string = this.translateService.instant('BOOKING_DETAILS_DIALOG_FAILED_MESSAGE', {
              0: this.selectedBookingDetails.pkBookingId,
            });
            const ok: string = translate.OK;

            this.pasConfirmationService.showConfirmation(title, message, ok, '', false).then(
              () => {},
              () => {},
            );
          });
      }
      this.getReferenceBookings();
      this.getBookingApprovalsHistory();
      this.getAttachments();
      this.onCommentChanged();
    } else {
      // First item is always existing on the form
      this.partialPaymentFormArray.at(0).get('dueDate').clearValidators();
      this.partialPaymentFormArray.at(0).get('dueDate').clearAsyncValidators();

      this.populateForm(this.referencedBooking);
      this.setRecourseTypeDropdownValues();
      if (this.referencedBooking) {
        this.getSelectedRecourseTypeDropdownValue(this.referencedBooking.recourseTypeId);
      } else {
        this.preSelectRecourseTypeDropdownValue();
      }
    }
    this.onChanges();
  }

  private checkIfInMeantimeManipulated(): void {
    this.isBookingValid = true;
    if (!this.isNewTabActive() && this.recourseBookingStore.selectedRecourseBookingId) {
      this.translateService
        .get([
          'AUTHENTICITY_RECOURSE_BOOKING_FAILED_TITLE',
          'AUTHENTICITY_RECOURSE_BOOKING_ATTACHMENTS_FAILED_TITLE',
          'ERROR_CODE_8004',
          'ERROR_CODE_8005',
        ])
        .subscribe((translate: { [key: string]: any }) => {
          let title: string;
          let content: string;
          if (!this.recourseBookingStore.selectedRecourseBooking.signatureValid) {
            this.isBookingValid = false;
            title = translate.AUTHENTICITY_RECOURSE_BOOKING_FAILED_TITLE;
            content = this.translateService.instant('ERROR_CODE_8004', {
              0: this.recourseBookingStore.selectedRecourseBookingId,
            });
            this.notificationServiceWrapper.addErrorNotification(title, content);
          }
          if (!this.recourseBookingStore.selectedRecourseBooking.signatureValidAttachments) {
            this.isBookingValid = false;
            title = translate.AUTHENTICITY_RECOURSE_BOOKING_ATTACHMENTS_FAILED_TITLE;
            content = this.translateService.instant('ERROR_CODE_8005', {
              0: this.recourseBookingStore.selectedRecourseBookingId,
            });
            this.notificationServiceWrapper.addErrorNotification(title, content);
          }
        });
    }
  }

  // eslint-disable-next-line complexity
  private populateForm(bookingDetails: RecourseBookingModel): void {
    this.bookingLoaded = true;
    if (this.isEditOrViewTabActive()) {
      this.formGroup.get('pkBookingId').setValue(bookingDetails.pkBookingId);
      this.formGroup
        .get('status')
        .setValue(translateBookingState(bookingDetails.bookingStateId, this.translateService));
      this.formGroup.get('recourseTypeId').setValue(bookingDetails.recourseTypeId);
      this.formGroup.get('createDate').setValue(bookingDetails.createDate);
      this.formGroup.get('creator').setValue(bookingDetails.creator);
      this.formGroup.get('bookingStateId').setValue(bookingDetails.bookingStateId);
      this.formGroup.get('invoiceReasonText').setValue(bookingDetails.invoiceReasonText);
      this.formGroup.get('bookingJustificationComment').setValue(bookingDetails.bookingJustificationComment);
      this.addRemoveRevenueOrderNumberControl(
        this.isBookingRecourseTypeRaD(this.selectedBookingDetails),
        bookingDetails.revenueOrderNumber,
      );

      if (bookingDetails.partialPaymentItems[0].dueDate && !isEmpty(bookingDetails.partialPaymentItems[0].dueDate)) {
        this.explicitPaymentTarget = true;
        this.formGroup.get('explicitPayment').setValue(true);
      } else {
        this.explicitPaymentTarget = false;
        this.formGroup.get('explicitPayment').setValue(false);
      }

      this.translateService.get(['RECOURSE', 'CAMPAIGN', 'RAD', 'AFTERSALES']).subscribe(translation => {
        if (bookingDetails.recourseTypeId === RecourseTypeIdEnum.RECOURSE) {
          this.recourseTypeLabel = translation.RECOURSE;
        } else if (bookingDetails.recourseTypeId === RecourseTypeIdEnum.CAMPAIGN) {
          this.recourseTypeLabel = translation.CAMPAIGN;
        } else if (this.isBookingRecourseTypeRaD(bookingDetails)) {
          this.recourseTypeLabel = translation.RAD;
        } else if (bookingDetails.recourseTypeId === RecourseTypeIdEnum.AFTERSALES) {
          this.recourseTypeLabel = translation.AFTERSALES;
        }
      });

      if (
        bookingDetails.bookingStateId === BookingStateEnum.IN_PROGRESS ||
        bookingDetails.bookingStateId === BookingStateEnum.REJECTED
      ) {
        this.startApprovalBtnHidden = false;
        this.startApprovalBtnDisabled = false;
      } else {
        this.startApprovalBtnHidden = true;
        this.startApprovalBtnDisabled = true;
      }

      const partialPaymentVar = this.setAmountAndEurAmountAndEditorCurrency(bookingDetails);
      this.partialPaymentFormArray.at(0).get('editorCurrency').setValue(partialPaymentVar.editorCurrency);
      this.partialPaymentFormArray
        .at(0)
        .get('amount')
        .setValue(partialPaymentVar.editorCurrency === 'EUR' ? partialPaymentVar.eurAmount : partialPaymentVar.amount);
      this.partialPaymentFormArray.at(0).get('dueDate').setValue(bookingDetails.partialPaymentItems[0].dueDate);

      this.selectedEditorCurrency = getDropdownValue(partialPaymentVar.editorCurrency);
      this.selectedBookingType = getBookingTypeDropdownValue(bookingDetails.bookingType, this.translateService);

      if (this.isEditTabActive()) {
        this.formGroup.enable();
        this.enablePartialPaymentFormArrayControls(true);
      } else if (this.isViewTabActive()) {
        this.populateSupplierData(bookingDetails.supplierData);
        this.formGroup.disable();
        this.enablePartialPaymentFormArrayControls(false);
      }
    }

    // TODO refactor the logic for creating manual booking from/without reference
    // this is done throughout the whole component in multiple places
    // 1.new booking; 2.references from campaign; 3.reference from audit report/another booking
    if (this.isNewTabActive()) {
      this.formGroup.enable();
      this.enablePartialPaymentFormArrayControls(true);
      this.selectedEditorCurrency =
        !bookingDetails || !bookingDetails.partialPaymentItems
          ? getDropdownValue('EUR') // Coming from Campaign one New Manual booking
          : getDropdownValue(bookingDetails.partialPaymentItems[0].editorCurrency.trim()); // If we came by Create correction booking button
      this.startApprovalBtnHidden = false;
      this.startApprovalBtnDisabled = true;
      this.addRemoveRevenueOrderNumberControl(this.isBookingRecourseTypeRaD(bookingDetails), '');
    }

    if (bookingDetails) {
      if (bookingDetails.bookingStateId === BookingStateEnum.FAILED) {
        this.bookingStateBlockedOrFailed = true;
        this.bookingStateReasonLabel = bookingDetails.bookingStateReason;
      }
      this.bookingCorrupted = !bookingDetails.signatureValid;
      this.formGroup.get('supplier').setValue(bookingDetails.supplierData.supplier);
      if (this.isNewTabActive() && this.referencedBooking) {
        this.onSupplierChanged();
        this.formGroup.get('supplier').disable();
        if (this.referencedBooking.partialPaymentItems && this.referencedBooking.partialPaymentItems[0]) {
          const eurAmount: number = this.referencedBooking.partialPaymentItems[0].eurAmount;
          this.partialPaymentFormArray.at(0).get('amount').setValue(eurAmount);
        }
        if (this.routerService.getPreviousUrl() === '/recourseCampaign') {
          const bookingTypeDebit = getBookingTypeDropdownValue(BookingTypeEnum.DEBIT, this.translateService);
          this.selectedBookingType = bookingTypeDebit;
          this.formGroup.get('bookingTypeDropdown').setValue(bookingTypeDebit);
        } else if (startsWith(this.routerService.getPreviousUrl(), '/recourseBooking')) {
          this.formGroup.get('bookingTypeDropdown').disable();
          const bookingTypeCredit = getBookingTypeDropdownValue(BookingTypeEnum.CREDIT, this.translateService);
          this.selectedBookingType = bookingTypeCredit;
          this.formGroup.get('bookingTypeDropdown').setValue(bookingTypeCredit);
          this.formGroup
            .get('invoiceReasonText')
            .setValue(
              this.getInvoiceReasonTextForCorrectionBooking(
                this.referencedBooking.invoiceReasonText,
                this.translateService,
              ),
            );
        }
        this.formGroup.get('recourseTypeDropdown').disable();
      }
      if (this.isEditTabActive()) {
        this.onSupplierChanged();
      }
    }

    this.setInitialFormGroupAndArrayValue();
    this.formInitialized = true;
  }

  private setAmountAndEurAmountAndEditorCurrency(bookingDetails: RecourseBookingModel): BookingPartialPaymentModel {
    const partialPaymentVar: BookingPartialPaymentModel = new BookingPartialPaymentModel();
    let editorCurrency: string;
    let amount: number;
    let eurAmount: number;
    if (this.isEditTabActive() && bookingDetails.bookingCurrency != bookingDetails.supplierData.currency.trim()) {
      editorCurrency = 'EUR';
      amount = bookingDetails.partialPaymentItems[0].eurAmount;
      eurAmount = bookingDetails.partialPaymentItems[0].eurAmount;
      this.currencyBookingAndCustomerNotEqual = true;
    } else {
      editorCurrency = bookingDetails.partialPaymentItems[0].editorCurrency.trim();
      amount = bookingDetails.partialPaymentItems[0].amount;
      eurAmount = bookingDetails.partialPaymentItems[0].eurAmount;
      this.currencyBookingAndCustomerNotEqual = false;
    }
    partialPaymentVar.editorCurrency = editorCurrency;
    partialPaymentVar.amount = amount;
    partialPaymentVar.eurAmount = eurAmount;

    return partialPaymentVar;
  }

  private getInvoiceReasonTextForCorrectionBooking(invoiceReason: any, translateService: TranslateService): string {
    const value = translateService.instant('INVOICE_REASON_TEXT_CORRECTION_BOOKING');
    return `${value} ${invoiceReason}`;
  }

  public getBookingApprovalsHistory(): void {
    this.approvalsHistoryService
      .getBookingApprovalsHistory(this.recourseBookingStore.selectedRecourseBookingId)
      .subscribe((approvalsHistory: BookingApprovalsHistoryModel[]) => {
        if (approvalsHistory.length > 0) {
          this.bookingApprovalsHistoryAccordionClosed = false;
          this.bookingApprovalsHistory = approvalsHistory;
          if (this.approvalsHistoryComponent) {
            this.approvalsHistoryComponent.refreshTable(approvalsHistory);
          }
          this.isBookingRejectionCommentVisible();
        }
      });
  }

  // Only for Edit and View dialog
  public getAttachments(): void {
    if (this.selectedBookingDetails.attachments.length > 0) {
      this.bookingAttachmentsAccordionClosed = false;
      this.selectedBookingDetails.attachments.forEach(attachment => {
        handleVerification(this.notificationServiceWrapper, this.translateService, attachment);
      });
      this.attachments = this.selectedBookingDetails.attachments;
    } else {
      this.bookingAttachmentsAccordionClosed = true;
    }
    if (this.bookingAttachmentsComponent) {
      this.bookingAttachmentsComponent.refreshTable(this.selectedBookingDetails.attachments);
    }
  }

  public isBookingRejectionCommentVisible(): void {
    this.bookingRejectionCommentVisible = false;
    // if booking is REJECTED, last entry from bookingApprovalsHistory
    // (which contains the rejection comment) has to be present
    if (this.recourseBookingStore.selectedRecourseBooking.bookingStateId === BookingStateEnum.REJECTED) {
      this.bookingRejectionCommentVisible = true;
    }
  }

  public isSignatureOk(): boolean {
    return this.isBookingValid;
  }

  private setInitialFormGroupAndArrayValue(): void {
    this.initialFormGroupAndArrayFields = cloneDeep(this.formGroup.value);
    this.initialFormGroupAndArrayFields.bookingTypeDropdown = cloneDeep(this.selectedBookingType);
    this.initialFormGroupAndArrayFields.editorCurrency = cloneDeep(this.selectedEditorCurrency);
  }

  public onSelectEditorCurrency(value: any): void {
    this.selectedEditorCurrency = typeof value === 'object' ? value : getDropdownValue(value);
    this.partialPaymentFormArray.controls.forEach((control, arrayIndex) => {
      this.calculateAmount(arrayIndex);
    });
  }

  public calculateAmount(index: number): void {
    const selectedCurrencyKeyIsNotEuro = !this.isEuroCurrencyKeySelected();
    const amountEntered: number = this.partialPaymentFormArray.at(index).get('amount').value;

    if (amountEntered == null || isEmpty(amountEntered.toString())) {
      this.resetTotalCalculatedAmountPerRow(index);
    }

    const selectedCurrency: string = this.selectedEditorCurrency.key;
    this.setReferringCurrencyVisibility(index);

    const currencyRateDate = this.isNewTabActive() ? new Date() : this.selectedBookingDetails.currencyRateDate;

    if (amountEntered && selectedCurrencyKeyIsNotEuro) {
      if (this.isViewTabActive()) {
        this.calculatedEUR[index] = this.selectedBookingDetails.partialPaymentItems[0].eurAmount;
        this.adaptFurtherValuesAfterAmountCalculation(
          index,
          amountEntered,
          null,
          RecourseBookingAddEditDetailsComponent.SELECTED_CURRENCY,
        );
      } else {
        const amountObj: Observable<AmountModel> = this.currencyService.convertAmountToSpecificCurrency(
          Number(amountEntered),
          selectedCurrency,
          'EUR',
          currencyRateDate,
        );
        amountObj.subscribe(result => {
          this.calculatedEUR[index] = result.transformedAmount;
          this.adaptFurtherValuesAfterAmountCalculation(
            index,
            amountEntered,
            null,
            RecourseBookingAddEditDetailsComponent.SELECTED_CURRENCY,
          );
        });
      }
    } else if (amountEntered && this.supplier && !isCustomerCurrencyEuro(this.supplier)) {
      const customerCurrency = this.supplier.currency.trim();

      if (this.isViewTabActive()) {
        this.calculatedForign[index] = this.selectedBookingDetails.partialPaymentItems[0].amount;
        this.adaptFurtherValuesAfterAmountCalculation(
          index,
          amountEntered,
          customerCurrency,
          RecourseBookingAddEditDetailsComponent.CUSTOMER_CURRENCY,
        );
      } else {
        const amountObj: Observable<AmountModel> = this.currencyService.convertAmountToSpecificCurrency(
          Number(amountEntered),
          'EUR',
          customerCurrency,
          currencyRateDate,
        );
        amountObj.subscribe(result => {
          this.calculatedForign[index] = result.transformedAmount;
          this.adaptFurtherValuesAfterAmountCalculation(
            index,
            amountEntered,
            customerCurrency,
            RecourseBookingAddEditDetailsComponent.CUSTOMER_CURRENCY,
          );
        });
      }
    } else {
      this.adaptFurtherValuesAfterAmountCalculation(index, amountEntered, null, null);
    }
  }

  private adaptFurtherValuesAfterAmountCalculation(
    index: number,
    amountEntered: number,
    customerCurrency: string,
    furtherValueAdaption: string,
  ): void {
    switch (furtherValueAdaption == null ? 'DEFAULT' : furtherValueAdaption) {
      case RecourseBookingAddEditDetailsComponent.SELECTED_CURRENCY: {
        this.calculatedForign[index] = Number(amountEntered);
        this.formattedCalculatedAmount[index] = `EUR: ${new DecimalFormatConverter(
          this.userService.userLanguage as UserLanguage,
        ).transformByGivenSeparator(this.calculatedEUR[index])}`;
        break;
      }
      case RecourseBookingAddEditDetailsComponent.CUSTOMER_CURRENCY: {
        this.calculatedEUR[index] = Number(amountEntered);
        this.formattedCalculatedAmount[index] = `${customerCurrency}: ${new DecimalFormatConverter(
          this.userService.userLanguage as UserLanguage,
        ).transformByGivenSeparator(this.calculatedForign[index])}`;
        break;
      }
      default: {
        this.calculatedEUR[index] = Number(amountEntered);
        this.formattedCalculatedAmount[index] = '';
        break;
      }
    }
    this.formatAndCalcTotalAmount();
  }

  private resetTotalCalculatedAmountPerRow(index: number): void {
    this.referringCurrencyVisible.splice(index, 1);

    this.calculatedEUR.splice(index, 1);
    this.calculatedForign.splice(index, 1);

    this.formatAndCalcTotalAmount();
  }

  private formatAndCalcTotalAmount(): void {
    this.totalCalculatedEUR =
      this.calculatedEUR.length > 1 ? this.calculatedEUR.reduce((c, d) => c + d) : this.calculatedEUR[0];
    this.totalCalculatedForign =
      this.calculatedForign.length > 1 ? this.calculatedForign.reduce((a, b) => a + b) : this.calculatedForign[0];

    if (this.totalCalculatedEUR > 0 && this.totalCalculatedForign > 0) {
      this.formattedTotalCalculatedAmount = `${this.totalCalculatedEUR.toFixed(
        2,
      )} € / ${this.totalCalculatedForign.toFixed(2)} ${this.customerCurrency}`;
      this.formattedTotalCalculatedAmount = `${new DecimalFormatConverter(
        this.userService.userLanguage as UserLanguage,
      ).transformByGivenSeparator(this.totalCalculatedEUR)} € / ${new DecimalFormatConverter(
        this.userService.userLanguage as UserLanguage,
      ).transformByGivenSeparator(this.totalCalculatedForign)} ${this.customerCurrency}`;
    } else if (this.totalCalculatedEUR > 0) {
      this.formattedTotalCalculatedAmount = `${new DecimalFormatConverter(
        this.userService.userLanguage as UserLanguage,
      ).transformByGivenSeparator(this.totalCalculatedEUR)} €`;
    } else {
      this.formattedTotalCalculatedAmount = '';
    }
  }

  public onAmountChanged(index: any): void {
    const amountEntered: string = this.partialPaymentFormArray.at(index).get('amount').value;
    // ONLY in first row we choose currency
    const currencySelected: string = this.partialPaymentFormArray.at(0).get('editorCurrency').value;
    if (amountEntered == null || isEmpty(amountEntered.toString())) {
      this.formattedCalculatedAmount[index] = '';
      this.referringCurrencyVisible[index] = false;
      this.calculatedEUR[index] = 0;
      this.calculatedForign[index] = 0;

      this.formatAndCalcTotalAmount();
    } else {
      this.onSelectEditorCurrency(currencySelected);
    }
  }

  public onExplicitPaymentTargetChanged(): void {
    this.explicitPaymentTarget = this.formGroup.get('explicitPayment').value;
    // Only for the first row of partial payment fields when it's possible to click on Explicit checkbox
    const dueDate: AbstractControl = this.partialPaymentFormArray.at(0).get('dueDate');
    if (this.explicitPaymentTarget) {
      dueDate.setValidators(this.dateNotBeforeToday());
    } else {
      dueDate.clearValidators();
      dueDate.clearAsyncValidators();
      dueDate.setErrors(null);
      dueDate.setValue(null);
    }
  }

  private dateNotBeforeToday(): ValidatorFn {
    return (control: UntypedFormControl): any => {
      if (this.isNewTabActive() && (!control.value || !moment(control.value).isSameOrAfter(new Date(), 'day'))) {
        return {
          dateNotBeforeToday: true,
        };
      } else if (this.isEditOrViewTabActive() && this.explicitPaymentTarget && !control.value) {
        return {
          dateNotFilled: true,
        };
      } else {
        return null;
      }
    };
  }

  private setReferringCurrencyVisibility(index: number): void {
    let foreignCurrency: string;

    if (this.isViewTabActive()) {
      foreignCurrency = this.selectedBookingDetails.bookingCurrency;
    } else {
      if (this.supplier) foreignCurrency = this.supplier.currency.trim();
    }
    this.referringCurrencyVisible[index] = foreignCurrency != 'EUR';
  }

  private isEuroCurrencyKeySelected(): boolean {
    if (this.selectedEditorCurrency && this.selectedEditorCurrency.key) {
      const currencyKey = this.selectedEditorCurrency.key.trim();
      return currencyKey === 'EUR';
    }
    return false;
  }

  public onSupplierChanged(): void {
    const supplierControl: any = this.formGroup.get('supplier');
    const supplierValue: string = supplierControl.value;
    if (isEmpty(supplierValue) || supplierValue.length < 6) {
      this.clearSupplierData();
    } else {
      this.supplierService.getSupplier({ supplierId: supplierValue }).subscribe({
        next: supplierData => {
          if (supplierData?.name) {
            this.populateSupplierData(supplierData);
            this.errorMessages = supplierData.customerErrorMessages;
          } else {
            supplierControl.setErrors({ incorrect: true });
            this.clearSupplierData();
          }
        },
        error: () => {
          supplierControl.setErrors({ incorrect: true });
          this.clearSupplierData();
        },
      });
    }
  }

  public noop(): void {
    // noop => intentionally emtpy (not beautiful but making it compilable)
  }

  private setRecourseTypeDropdownValues(): void {
    const rights: UserRights[] = this.userService.getRights();
    if (rights.includes(UserRights.RECOURSE_BOOKING)) {
      this.recourseTypeDropdownValues.push(this.recourseTypeDropdownValueCreator(RecourseTypeIdEnum.RECOURSE));
    }
    if (rights.includes(UserRights.CAMPAIGNS_BOOKING)) {
      this.recourseTypeDropdownValues.push(this.recourseTypeDropdownValueCreator(RecourseTypeIdEnum.CAMPAIGN));
    }
    if (rights.includes(UserRights.RAD_BOOKING)) {
      this.recourseTypeDropdownValues.push(this.recourseTypeDropdownValueCreator(RecourseTypeIdEnum.RAD));
    }
    if (rights.includes(UserRights.AFTERSALES_BOOKING)) {
      this.recourseTypeDropdownValues.push(this.recourseTypeDropdownValueCreator(RecourseTypeIdEnum.AFTERSALES));
    }
  }

  private recourseTypeDropdownValueCreator(recourseType: RecourseTypeIdEnum): any {
    return {
      key: recourseType,
      value: this.translateService.instant(getEnumStringValue(RecourseTypeIdEnum, recourseType)),
    };
  }

  /**
   * Precondition for user to open 'new booking' view is to have atleast one of booking user rights.
   * Therefore, this list can't be empty - it must have at least one recourseType.
   * Pre-selection logic: if dropdown contains RecourseTypeIdEnum.RECOURSE, pre-select it;
   * if not - then just select the first one from the list
   */
  private preSelectRecourseTypeDropdownValue(): void {
    if (this.recourseTypeDropdownValues.includes(this.recourseTypeDropdownValueCreator(RecourseTypeIdEnum.RECOURSE))) {
      this.selectedRecourseType = this.recourseTypeDropdownValueCreator(RecourseTypeIdEnum.RECOURSE);
    } else {
      this.selectedRecourseType = this.recourseTypeDropdownValues[0];
      this.addRemoveRevenueOrderNumberControl(this.selectedRecourseType.key === RecourseTypeIdEnum.RAD, '');
    }
  }

  /**
   * Pre-selects recourse type dropdown with value from referenced booking
   * (recourseTypeId is a non-nullable column in DB, it can't be null)
   */
  private getSelectedRecourseTypeDropdownValue(recourseTypeId: number): any {
    const recourseType: RecourseTypeIdEnum = getEnumByValue(RecourseTypeIdEnum, recourseTypeId);
    this.selectedRecourseType = this.recourseTypeDropdownValueCreator(recourseType);
  }

  private populateSupplierData(supplierData: SupplierDto): void {
    this.supplier = supplierData;
    this.formGroup.get('name').setValue(supplierData.name);
    this.formGroup.get('city').setValue(supplierData.city);
    this.debitor = supplierData.debitor;
    this.errorMessages = supplierData.customerErrorMessages;
    if (supplierData.currency) {
      this.customerCurrency = supplierData.currency.trim();
      this.addNewCurrencyToEditorCurrencyDropdown(supplierData.currency.trim());
    }
  }

  private clearSupplierData(): void {
    this.errorMessages = [];
    this.formGroup.get('name').setValue('');
    this.formGroup.get('city').setValue('');
    this.supplier = null;
    this.debitor = '';
    this.customerCurrency = '';
    this.partialPaymentFormArray.controls.forEach(control => {
      control.get('amount').setValue('');
      control.get('dueDate').setValue('');
    });
    this.resetToDefaultEditorCurrencyDropdownValues();
    this.selectedEditorCurrency = getDropdownValue('EUR');
  }

  private resetToDefaultEditorCurrencyDropdownValues(): void {
    this.recourseBookingStore.setCurrencyKeys([]);
    this.setEditorCurrencyDropdownValues();
  }

  private addNewCurrencyToEditorCurrencyDropdown(currency: string): void {
    this.resetToDefaultEditorCurrencyDropdownValues();
    if (!this.recourseBookingStore.currencyKeys.includes(currency)) {
      this.recourseBookingStore.currencyKeys.push(currency);
      this.setEditorCurrencyDropdownValues();
    }
    const currencyDropdownValue = this.getEditorCurrencyDropdownValue(currency);
    if (currencyDropdownValue) {
      this.partialPaymentFormArray.controls.forEach((control, index) => {
        this.calculateAmount(index);
      });
    }
  }

  private getEditorCurrencyDropdownValue(currencyKey: string): any {
    let currentlySelectedCurrencyKey = null;
    this.editorCurrencyDropdownValues.forEach(dropdownValue => {
      if (dropdownValue.key === currencyKey) {
        currentlySelectedCurrencyKey = dropdownValue;
      }
    });
    return currentlySelectedCurrencyKey;
  }

  private setBookingTypeDropdownValues(): void {
    this.bookingTypeDropdownValues = [
      getBookingTypeDropdownValue(BookingTypeEnum.CREDIT, this.translateService),
      getBookingTypeDropdownValue(BookingTypeEnum.DEBIT, this.translateService),
    ];
  }

  private setEditorCurrencyDropdownValues(): void {
    if (this.recourseBookingStore.currencyKeys.length === 0) {
      const defaultDropdownValue = 'EUR';
      this.recourseBookingStore.currencyKeys.push(defaultDropdownValue);
    }
    this.editorCurrencyDropdownValues = dropdownValuesHandler(this.recourseBookingStore.currencyKeys);
  }

  public ngAfterViewInit(): void {
    this.sidebarHandleService.enableSidebarForView();
  }

  public onFirstDataRendered(params): void {
    params.api.sizeColumnsToFit();
  }

  /**
   * const gridWidth maybe will be reactivated when the table functionality will be implemented
   */
  public onGridSizeChanged(params): void {
    const columnsToShow = [];
    const columnsToHide = [];
    params.api.setColumnsVisible(columnsToShow, true);
    params.api.setColumnsVisible(columnsToHide, false);
    params.api.sizeColumnsToFit();
  }

  private setFormValues(): void {
    this.formGroup = new UntypedFormGroup({
      pkBookingId: new UntypedFormControl(),
      status: new UntypedFormControl(),
      recourseTypeId: new UntypedFormControl(),
      createDate: new UntypedFormControl(),
      creator: new UntypedFormControl(),
      bookingStateId: new UntypedFormControl(),
      invoiceReasonText: new UntypedFormControl(
        {
          value: '',
          disabled: false,
        },
        { validators: [Validators.required] },
      ),
      recourseTypeDropdown: new UntypedFormControl(),
      bookingTypeDropdown: new UntypedFormControl(),
      explicitPayment: new UntypedFormControl(),
      bookingJustificationComment: new UntypedFormControl(),
      supplier: new UntypedFormControl(
        {
          value: '',
          disabled: false,
        },
        {
          validators: [Validators.required, Validators.pattern(/^\*$|^\d{6}$/)],
        },
      ),
      invoiceReason: new UntypedFormControl({ value: '', disabled: false }),
      name: new UntypedFormControl({ value: '', disabled: true }),
      city: new UntypedFormControl({ value: '', disabled: true }),
      referencedBookingId: new UntypedFormControl(),
      partialPaymentItems: this.createPartialPaymentFormArray(),
    });
  }

  private initValidationMessages(): void {
    this.validationMessages = [
      {
        /*00*/ error: 'required',
        message: this.translateService.instant('VALIDATION_INPUT_REQUIRED'),
      },
    ];
  }

  public onSelectBookingType(value: PasSelectOption | any): void {
    this.bookingTypeLabel = value;
    if (value) {
      this.selectedBookingType = getBookingTypeDropdownValue(value.key, this.translateService);
    }
  }

  public onSelectRecourseType(value: PasSelectOption | any): void {
    this.recourseTypeLabel = value;
    if (value) {
      this.addRemoveRevenueOrderNumberControl(value.key === RecourseTypeIdEnum.RAD, '');
    }
  }

  public isEditOrViewTabActive(): boolean {
    return this.isEditTabActive() || this.isViewTabActive();
  }

  public isEditTabActive(): boolean {
    return this.bookingRoutingService.isEditTabActive();
  }

  public isViewTabActive(): boolean {
    return this.bookingRoutingService.isViewTabActive();
  }

  public isNewTabActive(): boolean {
    return this.bookingRoutingService.isNewTabActive();
  }

  // populating accordions:
  public getReferenceBookings(): void {
    const selectedBooking = this.recourseBookingStore.selectedRecourseBooking;
    const referencedBookingId = selectedBooking.referencedBookingId ?? selectedBooking.pkBookingId;

    this.bookingService
      .getRecourseBookingReferenceBookings(referencedBookingId)
      .subscribe((referenceBookings: RecourseBookingModel[]) => {
        if (referenceBookings.length > 0) {
          this.referenceAccordionClosed = false;
          this.referenceBookings = referenceBookings;
        }
        if (this.referenceBookingsComponent) {
          this.referenceBookingsComponent.refreshTable(referenceBookings);
        }
      });
  }

  public canUserCancelBooking(): boolean {
    if (this.selectedBookingDetails !== undefined && this.selectedBookingDetails != null) {
      return isBookingCancelable(this.selectedBookingDetails, this.userService.getUser());
    }
    return false;
  }

  public canUserReprocess(): boolean {
    const rights: UserRights[] = this.userService.getRights();
    // Conditions:
    // Booking status: Blocked or failed booking (trrc_recourse_booking.FK_BOOKING_STATE_ID = 4 (blocked) or 11 (failed))
    // User has booking rights for this booking type (field.booking/campaign.booking)
    // No unsaved changes

    if (this.selectedBookingDetails && !this.isNewTabActive()) {
      return (
        (this.selectedBookingDetails.bookingStateId === BookingStateEnum.FAILED ||
          this.selectedBookingDetails.bookingStateId === BookingStateEnum.BLOCKED) &&
        checkBookingRightsForRecourseType(rights, this.selectedBookingDetails.recourseTypeId)
      );
    }
    return false;
  }

  public canUserDoCorrectionBooking(): boolean {
    const rights: UserRights[] = this.userService.getRights();

    return (
      !this.isNewTabActive() &&
      checkBookingRightsForRecourseType(rights, this.selectedBookingDetails.recourseTypeId) &&
      this.checkBookingTypeAndState(this.selectedBookingDetails) &&
      this.isSignatureOk()
    );
  }

  public checkBookingTypeAndState(selectedBooking: RecourseBookingModel): boolean {
    return (
      selectedBooking.bookingStateId === BookingStateEnum.BOOKED &&
      selectedBooking.bookingType === BookingTypeEnum.DEBIT
    );
  }

  public createCorrectionBooking(): void {
    this.bookingRoutingService.createNewBooking(this.selectedBookingDetails);
  }

  private setTopicDropdownValues(): void {
    const enumToArray: string[] = extractEnumValues(TopicEnum);
    this.topicDropdownValues = getDropdownValues(enumToArray, this.translateService);
  }

  public isUserReprocessEnabled(): boolean {
    let isBookingDateInsideCurrentMonth = false;
    if (this.recourseBookingStore.selectedRecourseBooking.bookingDate) {
      const currentDate = new Date();
      const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      const bookingDate = new Date(this.selectedBookingDetails.bookingDate.toString());

      isBookingDateInsideCurrentMonth = bookingDate >= firstDay && bookingDate <= lastDay;
    }

    return !this.formChanged && !this.bookingCorrupted && isBookingDateInsideCurrentMonth;
  }

  public onBtnCancel(): void {
    const currentUrl = this.routerService.getCurrentUrl();
    const previousUrl = this.routerService.getPreviousUrl();
    const recourseCampaignUrl = '/recourseCampaign';
    this.location.back();
    if (previousUrl === recourseCampaignUrl || currentUrl === recourseCampaignUrl) {
      // still using the old way with shared services until url mapping implementation on campaigns view
      this.campaignRoutingService.emitCancelToEditEvent(
        this.recourseBookingStore.recourseId,
        this.recourseBookingStore.recourseCampaignExId,
      );
    }
  }

  public isSaveButtonEnabled(): boolean {
    if (this.isNewTabActive()) {
      return this.formChanged && this.formGroup.valid;
    } else if (this.isEditTabActive()) {
      return (
        (this.formChanged && this.formGroup.valid) ||
        (!this.formChanged && !this.formGroup.invalid && this.currencyBookingAndCustomerNotEqual)
      );
    }
    return true;
  }

  public isStartApprovalButtonEnabled(): boolean {
    if (this.isNewTabActive()) {
      return this.formGroup.valid || (!this.startApprovalBtnDisabled && !this.startApprovalBtnHidden);
    }
    if (this.isEditOrViewTabActive()) {
      return (
        (this.formChanged && this.formGroup.valid) ||
        (!this.formChanged && !this.formGroup.invalid && !this.startApprovalBtnDisabled && !this.startApprovalBtnHidden)
      );
    }
    return true;
  }

  public isBookingLoaded(): boolean {
    return this.bookingLoaded;
  }

  public canUserApprove(): boolean {
    // Additional preconditions beside the User-Rights:
    // Current user can not be the same as creator and not be the same like the last editor.
    const invalidAttachments = !this.selectedBookingDetails.signatureValidAttachments;
    const manipulated = isManipulated(this.selectedBookingDetails) || invalidAttachments;
    const previousUrl = this.routerService.getPreviousUrl();

    return this.selectedBookingDetails && this.selectedBookingDetails.bookingStateId
      ? !manipulated &&
          (startsWith(previousUrl, '/approval') || startsWith(previousUrl, '/afterSales')) &&
          this.selectedBookingDetails.bookingStateId === BookingStateEnum.IN_APPROVAL &&
          canUserApproveBooking(this.userService.getUser(), this.selectedBookingDetails)
      : false;
  }

  public onBtnCancelBooking(): void {
    this.translateService
      .get(['CANCEL_BOOKING_TITLE', 'CANCEL_BOOKING_MESSAGE', 'OK', 'CANCEL'])
      .subscribe((translate: { [key: string]: any }) => {
        const title: string = translate.CANCEL_BOOKING_TITLE;
        const message: string = translate.CANCEL_BOOKING_MESSAGE;
        const ok: string = translate.OK;
        const dismiss: string = translate.CANCEL;

        this.pasConfirmationService.showConfirmation(title, message, ok, dismiss, false).then(
          () => {
            const bookingDetails = this.formGroup.value;
            this.convertDropdownValues(bookingDetails);
            this.bookingService.cancelBooking(this.formGroup.get('pkBookingId').value).subscribe({
              next: bookingId => {
                this.savedBookingIds.push(bookingId);
              },
              error: err => {
                this.showBtnCanceledFailedMessage(this.savedBookingIds, err);
              },
              complete: () => {
                this.showBtnCanceledSuccessMessage();
                // eslint-disable-next-line no-restricted-globals
                setTimeout(() => {
                  // if form is changed prior to cancel booking action, user is asked for confirmation of tab change
                  // therefore we set this value to false to skip the confirm dialog
                  this.formChanged = false;
                  this.bookingRoutingService.openBookingsOverview();
                }, 500);
              },
            });
          },
          () => {},
        );
      });
  }

  private showBtnCanceledSuccessMessage(): void {
    const selectedBooking = this.recourseBookingStore.selectedRecourseBooking;
    this.showSuccessMessage(selectedBooking.pkBookingId, 'TITLE_SAVED', 'CANCEL_BOOKING_SUCCESSFUL');
  }

  private showBtnCanceledFailedMessage(bookingIds: any, error: any): void {
    this.showFailureMessage(bookingIds, error, 'cancel', 'TITLE_FAILED', 'CANCEL_BOOKING_FAILED');
  }

  public onUploadAttachments(): void {
    // open upload dialog window
    this.acceptanceAllowed = this.checkUploadState();
    this.bookingAttachmentsAccordionClosed = false;
    this.uploadFileDialog = this.openDialogTemplate(this.uploadFileDialogTemplate);
    // this handles actions after dialog is closed
    this.uploadFileDialog.result.then(() => {
      this.onCloseUploadFileDialog();
    });
  }

  public onAcceptUploadFileDialog(): void {
    if (this.isNewTabActive()) {
      this.filesToUpload.push(cloneDeep(this.uploadFileModel));
      const attachmentModel = newAttachmentModel(
        this.userService,
        this.uploadFileModel,
        this.indexGenerator,
        this.dateFormatPipe,
      );
      this.attachments.push(attachmentModel);
      const newAttachments = [attachmentModel];
      this.bookingAttachmentsComponent.applyTransactionAdd(newAttachments);
      this.uploadFileModel.reset();
      this.uploadFileDialog.close();
    } else {
      this.processComponent.startProcess();
      const fileName = this.uploadFileModel.filename;

      const bookingIds: string[] = [];
      bookingIds.push(this.selectedBookingDetails.pkBookingId.toString());
      const formData = newFormDataForAttachment(
        this.uploadFileModel,
        this.uploadFileModel.topic.key,
        bookingIds,
        '',
        '',
      );

      this.attachmentService.upload(formData).subscribe({
        next: attachmentIds => {
          if (this.bookingAttachmentsAccordionClosed) {
            this.bookingAttachmentsAccordionClosed = false;
          }
          if (bookingStatusUpdateNeeded(this.selectedBookingDetails)) {
            this.updateBookingStatusOnAttachmentCreateDelete();
          }
          this.processComponent.stopProcess();
          this.bookingAttachmentsComponent.applyTransactionAdd(attachmentIds);
          showAttachmentUploadSuccess(this.notificationServiceWrapper, this.translateService, fileName);
        },
        error: err => {
          this.processComponent.stopProcess();
          showAttachmentUploadFailed(this.notificationServiceWrapper, this.translateService, fileName, err);
        },
        complete: () => {
          this.uploadFileModel.reset();
        },
      });
      this.uploadFileDialog.close();
    }
  }

  public onAttachmentDeletion(): void {
    this.updateBookingStatusOnAttachmentCreateDelete();
  }

  private updateBookingStatusOnAttachmentCreateDelete(): void {
    if (bookingStatusUpdateNeeded(this.selectedBookingDetails)) {
      this.recourseBookingStore.updateSelectedRecourseBookingState(BookingStateEnum.IN_PROGRESS);
      this.populateForm(this.recourseBookingStore.selectedRecourseBooking);
    }
  }

  public onCommentChanged(): void {
    const currentCommentVal: string = this.formGroup.get('bookingJustificationComment').value;
    if (currentCommentVal != null) {
      this.compCommentCharactersLeft = this.compCommentCharactersMaxLength - currentCommentVal.length;
    }
  }

  private onChanges(): void {
    if (this.isEditOrViewTabActive()) {
      this.formGroup.valueChanges.pipe(takeUntil(this.savedSubject$)).subscribe(newFormValue => {
        if (this.formInitialized && !isString(newFormValue.bookingTypeDropdown)) {
          const initialValue = this.initialFormGroupAndArrayFields;
          this.formChanged = this.isEditTabActive() && this.formValueChanged(initialValue, newFormValue);
          if (this.referenceBookingsComponent) {
            this.referenceBookingsComponent.parentFormDetailAddEditChanged = this.formChanged;
          }
          const dueDate = (
            (this.formGroup.get('partialPaymentItems') as UntypedFormArray).controls[0] as UntypedFormGroup
          ).controls.dueDate;
          if (this.explicitPaymentTarget && !dueDate) {
            dueDate.setErrors({ incorrect: true });
          }
        }
      });
    }

    if (this.isNewTabActive()) {
      this.formChanged = true;
      this.formGroup.valueChanges.subscribe(() => {
        if (!this.selectedRecourseType) {
          this.formGroup.get('recourseTypeDropdown').setErrors({ incorrect: true });
        }
        if (!this.selectedBookingType) {
          this.formGroup.get('bookingTypeDropdown').setErrors({ incorrect: true });
        }
        if (!this.selectedEditorCurrency) {
          (
            (this.formGroup.get('partialPaymentItems') as UntypedFormArray).controls[0] as UntypedFormGroup
          ).controls.editorCurrency.setErrors({ incorrect: true });
        }

        (this.formGroup.get('partialPaymentItems') as UntypedFormArray).controls.forEach(partialPaymentItem => {
          if (this.explicitPaymentTarget && !partialPaymentItem.get('dueDate').value) {
            partialPaymentItem.get('dueDate').setErrors({ incorrect: true });
          }
        });
      });
    }
  }

  // eslint-disable-next-line complexity
  private formValueChanged(initialFormGroupAndArrayFields, newFormValue): boolean {
    if (!isEqual(initialFormGroupAndArrayFields.bookingTypeDropdown, newFormValue.bookingTypeDropdown)) {
      return true;
    }
    if (!isEqual(initialFormGroupAndArrayFields.invoiceReasonText, newFormValue.invoiceReasonText)) {
      return true;
    }
    if (
      newFormValue.partialPaymentItems[0].amount === null ||
      initialFormGroupAndArrayFields.partialPaymentItems[0].amount === null
    ) {
      if (
        !isEqual(
          initialFormGroupAndArrayFields.partialPaymentItems[0].amount,
          newFormValue.partialPaymentItems[0].amount,
        )
      ) {
        return true;
      }
    } else {
      if (
        !isEqual(
          Number(initialFormGroupAndArrayFields.partialPaymentItems[0].amount).toFixed(2).toString(),
          Number(newFormValue.partialPaymentItems[0].amount).toFixed(2).toString(),
        )
      ) {
        return true;
      }
    }
    if (
      typeof initialFormGroupAndArrayFields.partialPaymentItems[0].editorCurrency === 'object' &&
      typeof newFormValue.partialPaymentItems[0].editorCurrency === 'object'
    ) {
      if (
        !isEqual(
          initialFormGroupAndArrayFields.partialPaymentItems[0].editorCurrency.key,
          newFormValue.partialPaymentItems[0].editorCurrency.key,
        )
      ) {
        return true;
      }
    } else if (
      typeof initialFormGroupAndArrayFields.partialPaymentItems[0].editorCurrency !== 'object' &&
      typeof newFormValue.partialPaymentItems[0].editorCurrency === 'object'
    ) {
      if (
        !isEqual(
          initialFormGroupAndArrayFields.partialPaymentItems[0].editorCurrency,
          newFormValue.partialPaymentItems[0].editorCurrency.key,
        )
      ) {
        return true;
      }
    } else if (
      typeof initialFormGroupAndArrayFields.partialPaymentItems[0].editorCurrency === 'object' &&
      typeof newFormValue.partialPaymentItems[0].editorCurrency !== 'object'
    ) {
      if (
        !isEqual(
          initialFormGroupAndArrayFields.partialPaymentItems[0].editorCurrency.key,
          newFormValue.partialPaymentItems[0].editorCurrency,
        )
      ) {
        return true;
      }
    } else {
      if (
        !isEqual(
          initialFormGroupAndArrayFields.partialPaymentItems[0].editorCurrency,
          newFormValue.partialPaymentItems[0].editorCurrency,
        )
      ) {
        return true;
      }
    }
    if (!isEqual(initialFormGroupAndArrayFields.explicitPayment, newFormValue.explicitPayment)) {
      return true;
    }
    if (initialFormGroupAndArrayFields.partialPaymentItems[0].dueDate && this.explicitPaymentTarget) {
      const dueDate = initialFormGroupAndArrayFields.partialPaymentItems[0].dueDate;
      const newDueDate = this.dateFormatPipe.transformDateToGlobalFormat(newFormValue.partialPaymentItems[0].dueDate);
      if (!isEqual(dueDate, newDueDate)) {
        return true;
      }
    } else {
      if (newFormValue.partialPaymentItems[0].dueDate && this.explicitPaymentTarget) {
        return true;
      }
    }
    if (!isEqual(initialFormGroupAndArrayFields.supplier, newFormValue.supplier)) {
      return true;
    }
    if (!isEqual(initialFormGroupAndArrayFields.revenueOrderNumber, newFormValue.revenueOrderNumber)) {
      return true;
    }
    if (
      !isEqual(initialFormGroupAndArrayFields.bookingJustificationComment, newFormValue.bookingJustificationComment)
    ) {
      return true;
    }
    if (!isEqual(this.recourseBookingStore.selectedRecourseBooking.supplierData.debitor, this.debitor)) {
      return true;
    }
  }

  public onCheckboxChanged(): void {
    this.acceptanceAllowed = this.checkUploadState();
  }

  public onFileInput(event: any): void {
    this.uploadFileModel.fileToUpload = event.target.files.item(0);
    if (this.uploadFileModel.fileToUpload.type === 'application/pdf') {
      this.externalReceiverCheckboxDisabled = false;
    } else {
      this.uploadFileModel.extern = false; // set checkbox state to unchecked
      this.externalReceiverCheckboxDisabled = true;
    }
    this.uploadFileModel.filename = this.uploadFileModel.fileToUpload.name;
    this.acceptanceAllowed = this.checkUploadState();
  }

  public isTopicVisible(): boolean {
    return false;
  }

  public isSelectedRecourseType(): boolean {
    if (this.isNewTabActive()) {
      return this.selectedRecourseType && this.selectedRecourseType.key === RecourseTypeIdEnum.RECOURSE;
    }
    if (this.isEditTabActive()) {
      return this.selectedBookingDetails.recourseTypeId === RecourseTypeIdEnum.RECOURSE;
    }
  }

  public isSelectedRaDType(): boolean {
    return this.selectedRecourseType && this.selectedRecourseType.key === RecourseTypeIdEnum.RAD;
  }

  public onSelectTopic(value: PasSelectOption | any): void {
    this.selectedTopic = value.key.toString();
  }

  public onCloseUploadFileDialog(): void {
    this.checkAccordionAttachmentsVisibility();
    this.uploadFileDialog.close();
    this.uploadFileModel.reset();
  }

  private checkUploadState(): boolean {
    // Only files <= 15MB are allowed
    return isNil(this.uploadFileModel.fileToUpload)
      ? false
      : this.uploadFileModel.fileToUpload.size <= this.maxFileUploadSize;
  }

  private checkAccordionAttachmentsVisibility(): void {
    if (this.isNewTabActive()) {
      this.bookingAttachmentsAccordionClosed = this.filesToUpload.length === 0;
    }
  }

  private convertDropdownValues(bookingDetails: any): void {
    if (bookingDetails) {
      bookingDetails.partialPaymentItems.forEach(item => {
        item.editorCurrency = this.selectedEditorCurrency.value;
      });

      if (this.selectedBookingType) {
        bookingDetails.bookingType = this.selectedBookingType.key;
      }
      if (this.isNewTabActive() && this.selectedRecourseType) {
        bookingDetails.recourseTypeId = this.selectedRecourseType.key;
        bookingDetails.recourseTypeShort = this.selectedRecourseType.value;
      }
    }
  }

  private saveNewRecourseBooking(): void {
    const bookingDetails = this.formGroup.value;
    const recourseBookingNew: RecourseBookingCUModel = new RecourseBookingCUModel();
    recourseBookingNew.invoiceReasonText = bookingDetails.invoiceReasonText;
    recourseBookingNew.supplierId = this.formGroup.get('supplier').value.trim();
    recourseBookingNew.partialPaymentItems = [...bookingDetails.partialPaymentItems];
    recourseBookingNew.bookingStateId = BookingStateEnum.IN_PROGRESS.valueOf();
    recourseBookingNew.revenueOrderNumber = bookingDetails.revenueOrderNumber;
    recourseBookingNew.bookingJustificationComment = bookingDetails.bookingJustificationComment;
    this.convertDropdownValues(recourseBookingNew);
    if (this.referencedBooking) {
      recourseBookingNew.referencedBookingId = this.referencedBooking.pkBookingId;
      recourseBookingNew.recourseId = this.referencedBooking.recourseId;
    }

    this.openModalSpinner();
    this.bookingService.createNewRecourseBooking(recourseBookingNew).subscribe({
      next: listOfBookingIds => {
        this.savedBookingIds = [...listOfBookingIds];
        this.uploadFiles(this.savedBookingIds);
      },
      error: err => {
        this.showSaveErrorMessage(err);
        this.closeModalSpinner();
      },
      complete: () => {
        this.showSaveSuccessMessageForNewBooking(this.savedBookingIds);
        this.closeSpinnerAndSwitchToOverview();
      },
    });
  }

  private showSaveErrorMessage(error: any): void {
    this.loggerService.error(`Failed to save recourse booking:${error.toString()}`);
    this.translateService
      .get(['SAVE_BOOKING_FAILED_TITLE', 'SAVE_BOOKING_FAILED_MESSAGE'])
      .subscribe((translate: { [key: string]: any }) => {
        const title: string = translate.SAVE_BOOKING_FAILED_TITLE;
        const content: string = translate.SAVE_BOOKING_FAILED_MESSAGE;
        this.notificationServiceWrapper.addErrorNotification(title, `${content}\n${error.message}`);
      });
  }

  private showSaveAndSignatureErrorMessage(errorResponse: any): void {
    this.loggerService.error(`Failed to save recourse booking:${errorResponse.toString()}`);
    let title = '';
    let content = '';
    if (errorResponse.error.errorCode === 8004) {
      this.translateService
        .get(['AUTHENTICITY_RECOURSE_BOOKING_FAILED_TITLE', 'ERROR_CODE_8004'])
        .subscribe((translate: { [key: string]: any }) => {
          if (!this.recourseBookingStore.selectedRecourseBooking.signatureValid) {
            this.isBookingValid = false;
            title = translate.AUTHENTICITY_RECOURSE_BOOKING_FAILED_TITLE;
            content = this.translateService.instant('ERROR_CODE_8004', {
              0: this.recourseBookingStore.selectedRecourseBookingId,
            });
            this.notificationServiceWrapper.addErrorNotification(title, content);
          }
        });
    } else if (errorResponse.error.errorCode === 8005) {
      this.translateService
        .get(['AUTHENTICITY_RECOURSE_BOOKING_ATTACHMENTS_FAILED_TITLE', 'ERROR_CODE_8005'])
        .subscribe((translate: { [key: string]: any }) => {
          if (!this.recourseBookingStore.selectedRecourseBooking.signatureValidAttachments) {
            this.isBookingValid = false;
            title = translate.AUTHENTICITY_RECOURSE_BOOKING_ATTACHMENTS_FAILED_TITLE;
            content = this.translateService.instant('ERROR_CODE_8005', {
              0: this.recourseBookingStore.selectedRecourseBookingId,
            });
            this.notificationServiceWrapper.addErrorNotification(title, content);
          }
        });
    } else {
      this.translateService
        .get(['SAVE_BOOKING_FAILED_TITLE', 'SAVE_BOOKING_FAILED_MESSAGE'])
        .subscribe((translate: { [key: string]: any }) => {
          title = translate.SAVE_BOOKING_FAILED_TITLE;
          content = translate.SAVE_BOOKING_FAILED_MESSAGE;
          this.notificationServiceWrapper.addErrorNotification(title, `${content}\n${errorResponse.message}`);
        });
    }
  }

  private showSaveSuccessMessageForNewBooking(savedBookingId: any): void {
    this.showSuccessMessage(savedBookingId, 'SAVED_NEW_BOOKING_TITLE', 'SAVED_NEW_BOOKING_SUCCESSFUL');
  }

  private showBtnStartApprovalSuccess(bookingDetailsId: any): void {
    this.showSuccessMessage(bookingDetailsId, 'TITLE_APPROVAL_WORKFLOW', 'APPROVAL_WORKFLOW_BOOKING_SUCCESSFUL');
  }

  private showBtnStartApprovalFailed(bookingDetailsId: any, error: any): void {
    this.showFailureMessage(
      bookingDetailsId,
      error,
      'start approval',
      'TITLE_FAILED',
      'APPROVAL_WORKFLOW_BOOKING_FAILED',
    );
  }

  private showApprovalNotification(): void {
    this.showSuccessMessage(this.savedBookingIds, 'TITLE_APPROVAL_WORKFLOW', 'APPROVAL_WORKFLOW_BOOKING_SUCCESSFUL');
  }

  public onBtnSave(): void {
    if (!this.explicitPaymentTarget) {
      this.partialPaymentFormArray.controls.forEach(control => {
        control.get('dueDate').setValue('');
      });
    }
    if (this.isEditOrViewTabActive()) {
      this.saveEditedRecourseBooking();
    } else if (this.isNewTabActive()) {
      this.saveNewRecourseBooking();
    }
  }

  private saveEditedRecourseBooking(): void {
    const bookingDetails: RecourseBookingCUModel = this.formGroup.value;
    bookingDetails.supplierId = this.supplier.supplier;

    this.convertDropdownValues(bookingDetails);

    if (
      bookingDetails.bookingStateId === BookingStateEnum.APPROVED ||
      bookingDetails.bookingStateId === BookingStateEnum.BLOCKED
    ) {
      bookingDetails.bookingStateId = BookingStateEnum.IN_APPROVAL;
    } else if (bookingDetails.bookingStateId === BookingStateEnum.FAILED) {
      bookingDetails.bookingStateId = BookingStateEnum.IN_PROGRESS;
    }

    this.openModalSpinner();
    this.bookingService.updateRecourseBookingDetails(bookingDetails).subscribe({
      next: bookingId => {
        this.savedBookingIds.push(bookingId);
      },
      error: err => {
        this.showSaveAndSignatureErrorMessage(err);
        this.closeModalSpinner();
      },
      complete: () => {
        this.showSaveSuccessMessageForNewBooking(bookingDetails.pkBookingId);
        this.closeSpinnerAndSwitchToOverview();
      },
    });
  }

  public onBtnStartApproval(): void {
    const bookingDetails: RecourseBookingCUModel = this.formGroup.value;
    this.convertDropdownValues(bookingDetails);
    bookingDetails.bookingStateId = BookingStateEnum.IN_APPROVAL;
    bookingDetails.supplierId = this.supplier.supplier;

    if (this.isEditOrViewTabActive()) {
      this.translateService
        .get(['APPROVAL_PROCESS_CONFIRMDIALOG_TITLE', 'APPROVAL_PROCESS_CONFIRMDIALOG_MESSAGE', 'OK', 'CANCEL'])
        .subscribe((translate: { [key: string]: any }) => {
          const title: string = translate.APPROVAL_PROCESS_CONFIRMDIALOG_TITLE;
          const message: string = translate.APPROVAL_PROCESS_CONFIRMDIALOG_MESSAGE;
          const ok: string = translate.OK;
          const dismiss: string = translate.CANCEL;

          this.pasConfirmationService.showConfirmation(title, message, ok, dismiss, true).then(
            () => {
              this.openModalSpinner();
              this.bookingService.startApproval(this.formGroup.get('pkBookingId').value).subscribe({
                next: bookingId => {
                  this.savedBookingIds.push(bookingId);
                },
                error: err => {
                  this.showBtnStartApprovalFailed(this.savedBookingIds, err);
                  this.closeModalSpinner();
                },
                complete: () => {
                  this.showBtnStartApprovalSuccess(bookingDetails.pkBookingId);
                  this.startApprovalBtnHidden = true;
                  this.startApprovalBtnDisabled = true;
                  this.closeSpinnerAndSwitchToOverview();
                },
              });
            },
            () => {},
          );
        });
    } else if (this.isNewTabActive()) {
      if (this.referencedBooking) {
        bookingDetails.referencedBookingId = this.referencedBooking.pkBookingId;
        bookingDetails.recourseId = this.referencedBooking.recourseId;
      }

      this.translateService
        .get(['APPROVAL_PROCESS_CONFIRMDIALOG_TITLE', 'APPROVAL_PROCESS_CONFIRMDIALOG_MESSAGE', 'OK', 'CANCEL'])
        .subscribe((translate: { [key: string]: any }) => {
          const title: string = translate.APPROVAL_PROCESS_CONFIRMDIALOG_TITLE;
          const message: string = translate.APPROVAL_PROCESS_CONFIRMDIALOG_MESSAGE;
          const ok: string = translate.OK;
          const dismiss: string = translate.CANCEL;

          this.pasConfirmationService.showConfirmation(title, message, ok, dismiss, true).then(
            () => {
              this.openModalSpinner();
              this.bookingService.startApprovalForNewRecourseBooking(bookingDetails).subscribe({
                next: listOfBookingIds => {
                  this.savedBookingIds = [...listOfBookingIds];
                  // avoid changing state of booking (that is just created and put in IN_APPROVAL) to IN_PROGRESS
                  const keepBookingState = true;
                  this.uploadFiles(this.savedBookingIds, keepBookingState);
                },
                error: err => {
                  this.showSaveErrorMessage(err);
                  this.closeModalSpinner();
                },
                complete: () => {
                  this.showApprovalNotification();
                  this.startApprovalBtnHidden = true;
                  this.startApprovalBtnDisabled = true;
                  this.closeSpinnerAndSwitchToOverview();
                },
              });
            },
            () => {},
          );
        });
    }
  }

  public canUserUploadAttachments(): boolean {
    if (this.isNewTabActive()) {
      return true;
    }
    return (
      this.isEditTabActive() &&
      checkBookingStateAndUserRights(
        this.userService.getRights(),
        this.recourseBookingStore.selectedRecourseBooking.recourseTypeId,
        this.recourseBookingStore.selectedRecourseBooking.bookingStateId,
        false,
      )
    );
  }

  /**
   * Method is called on init of component. It creates the corresponding formArray inside FormGroup.
   * @returns {returns FormArray with Control iniside }
   */
  private createPartialPaymentFormArray(): UntypedFormArray {
    // FormArray has to be seperated and inside FormGroup to get access to them in the template
    this.partialPaymentFormArray = new UntypedFormArray([]);
    this.partialPaymentFormArray.push(this.createFormItem());

    return this.partialPaymentFormArray;
  }

  /**
   * Adds a new partial payment-formGroup to the form and inside FormArray.
   */
  public addNewFormItem(): void {
    this.partialPaymentFormArray.push(this.createFormItem());
  }

  private createFormItem(): UntypedFormGroup {
    if (this.partialPaymentFormArray.length === 1 && !this.explicitPaymentTarget) {
      this.explicitPaymentTarget = true;
      this.formGroup.get('explicitPayment').setValue(true);
    }

    const createdFormControl = new UntypedFormGroup({
      amount: new UntypedFormControl(
        {
          value: '',
          disabled: false,
        },
        { validators: [Validators.required] },
      ),
      eurAmount: new UntypedFormControl({ value: '', disabled: false }),
      editorCurrency: new UntypedFormControl({ value: '', disabled: false }),
      dueDate: new UntypedFormControl({ value: '', disabled: false }),
    });
    const dueDate: AbstractControl = createdFormControl.get('dueDate');
    if (this.explicitPaymentTarget) {
      dueDate.setValidators(this.dateNotBeforeToday());
    } else {
      dueDate.clearValidators();
      dueDate.clearAsyncValidators();
      dueDate.setErrors(null);
      dueDate.setValue(null);
    }

    const editorCurrency: AbstractControl = createdFormControl.get('editorCurrency');
    if (this.partialPaymentFormArray.controls.length > 0) {
      editorCurrency.disable();
    }

    return createdFormControl;
  }

  /**
   * Removes the corresponding row partialPayment-formGroup.
   * @param {number} itemIndex
   */
  public removeFormItem(itemIndex: number): void {
    // Order of next 2 line is important
    this.partialPaymentFormArray.removeAt(itemIndex);
    this.resetTotalCalculatedAmountPerRow(itemIndex);
  }

  private enablePartialPaymentFormArrayControls(enable: boolean): void {
    this.partialPaymentFormArray.controls.forEach((control, index) => {
      if (enable) {
        if (index === 0) {
          control.enable();
        } else {
          control.get('amount').enable();
          control.get('editorCurrency').disable();
          control.get('dueDate').enable();
        }
      } else {
        control.disable();
      }
    });
  }

  private uploadFiles(savedBookingIds?: any[], keepBookingState?: boolean): void {
    this.filesToUpload.forEach(fileToUpload => {
      const fileName = fileToUpload.filename;
      const formData = newFormDataForAttachment(fileToUpload, fileToUpload.topic.key, savedBookingIds, '', '');
      this.attachmentService.upload(formData, keepBookingState).subscribe({
        next: () => {
          showAttachmentUploadSuccess(this.notificationServiceWrapper, this.translateService, fileName);
        },
        error: err => {
          showAttachmentUploadFailed(this.notificationServiceWrapper, this.translateService, fileName, err);
        },
      });
    });
  }

  public async onBtnApproveBooking(): Promise<void> {
    const result = await this.approveRejectReprocessDialog.onBtnApproveBooking();
    if (result) {
      this.updateApprovalsHistoryComponent();
      this.updateRecourseBooking();
    }
  }

  public async onBtnRejectBooking(): Promise<void> {
    const result = await this.approveRejectReprocessDialog.onBtnRejectBooking();
    if (result) {
      this.updateApprovalsHistoryComponent();
      this.updateRecourseBooking();
    }
  }

  public async onBtnReprocessBooking(): Promise<void> {
    const result = await this.approveRejectReprocessDialog.onBtnReprocessBooking();
    if (result) {
      this.updateApprovalsHistoryComponent();
      this.updateRecourseBooking();
    }
  }

  private updateRecourseBooking(): void {
    this.recourseBookingObservable = this.bookingService.getRecourseBookingDetails(
      this.recourseBookingStore.selectedRecourseBookingId,
    );
    this.recourseBookingObservable.subscribe((bookingDetails: RecourseBookingModel) => {
      // ToDo: We should decide where the val will be stored. In store or in tsController
      this.selectedBookingDetails = bookingDetails;
      this.recourseBookingStore.setSelectedRecourseBooking(bookingDetails);
      this.populateForm(this.selectedBookingDetails);
    });
  }

  public updateApprovalsHistoryComponent(): void {
    const selectedBooking = this.recourseBookingStore.selectedRecourseBooking;
    if (
      selectedBooking &&
      selectedBooking.bookingStateId !== BookingStateEnum.BLOCKED &&
      selectedBooking.bookingStateId !== BookingStateEnum.FAILED
    ) {
      this.approvalsHistoryService
        .getBookingApprovalsHistory(this.recourseBookingStore.selectedRecourseBookingId)
        .subscribe((approvalsHistory: BookingApprovalsHistoryModel[]) => {
          this.bookingApprovalsHistory = approvalsHistory;
          if (approvalsHistory.length > 0) {
            this.bookingApprovalsHistoryAccordionClosed = false;
          }
          this.approvalsHistoryComponent.refreshTable(this.bookingApprovalsHistory);
        });
    }
  }

  private openDialogTemplate(dialogTemplate: ElementRef): NgbModalRef {
    return this.modalService.open(dialogTemplate, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      size: 'lg',
    });
  }

  private showSuccessMessage(bookingId: any, messageTitle: string, messageContent: string): void {
    this.translateService.get([messageTitle, messageContent]).subscribe((translate: { [key: string]: any }) => {
      const title: string = translate[messageTitle];
      const content: string = this.translateService.instant(messageContent, { 0: bookingId });
      this.notificationServiceWrapper.addSuccessNotification(title, content);
    });
  }

  private showFailureMessage(
    bookingId: any,
    error: any,
    action: string,
    messageTitle: string,
    messageContent: string,
  ): void {
    this.loggerService.error(`Failed to ${action} recourse booking:${error.toString()}`);
    this.translateService.get([messageTitle, messageContent]).subscribe((translate: { [key: string]: any }) => {
      const title: string = translate[messageTitle];
      const content: string = this.translateService.instant(messageContent, { 0: bookingId });
      this.notificationServiceWrapper.addErrorNotification(title, content);
    });
  }

  private openModalSpinner(): void {
    // open full-screen modal spinner
    this.modalSpinner = this.modalService.open(this.modalSpinnerTemplate, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      size: 'lg',
      windowClass: 'modal-spinner',
    });
  }

  private closeModalSpinner(): void {
    this.modalSpinner.close();
  }

  private isBookingRecourseTypeRaD(booking: RecourseBookingModel): boolean {
    return booking && booking.recourseTypeId === RecourseTypeIdEnum.RAD;
  }

  /**
   * Adds or removes "revenueOrderNumber" control, together with validators
   * Pattern: constant '402' + 7 further digits
   * @param add - should control be added, else control removed
   * @param fieldValue - value of control
   */
  private addRemoveRevenueOrderNumberControl(add: boolean, fieldValue: string): void {
    const controlName = 'revenueOrderNumber';
    if (add) {
      this.formGroup.addControl(
        controlName,
        new UntypedFormControl(
          {
            value: fieldValue,
            disabled: false,
          },
          { validators: [Validators.required, Validators.pattern(/^\*$|^402\d{7}$/)] },
        ),
      );
    } else {
      this.formGroup.removeControl(controlName);
    }
  }

  private closeSpinnerAndSwitchToOverview(): void {
    this.savedSubject$.next();
    this.formChanged = false;
    // eslint-disable-next-line no-restricted-globals
    setTimeout(() => {
      this.bookingRoutingService.openBookingsOverview();
      this.closeModalSpinner();
    }, 500);
  }

  public createDeleteAttachmentAllowed(): boolean {
    if (this.isNewTabActive()) {
      return true;
    }
    return createDeleteAttachmentAllowed(this.selectedBookingDetails);
  }
}
